import { useMemo, useState } from 'react';
import { ArrowsUpDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

import { InsurerLogo } from '@/components/common';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn, formatCurrency } from '@/utils';
import { addonEntries, addonLabel, type AddonCover } from '@/utils/addons';
import {
  COVER_DESCRIPTIONS,
  COVER_LABELS,
  COVER_ORDER,
  optionPremium as premiumOf,
  type CoverType,
} from '@/utils/coverTypes';

/**
 * 24 Sep -- the owner: "if there are 10 insurance companies quoting it would
 * become difficult and we cannot even compare".
 *
 * One card per insurer x cover turned ten insurers into 20-30 cards. This draws
 * the SAME options as one comparison: a row per insurer, a column per cover.
 * It is presentation only -- every figure is read off the option the API sent,
 * and choosing still hands the parent one quote id for the unchanged accept.
 *
 * A price with no recorded cover (a premium an advisor typed in by hand) goes
 * in "Other / as quoted". Nothing here guesses a cover for it.
 */

export interface ComparableOption {
  id: string;
  insurer_name: string;
  insurer_logo?: string | null;
  pricing_source?: 'insurer_api' | 'manual';
  cover_type?: string | null;
  premium_breakdown?: { od_premium?: number | null; tp_premium?: number | null } | null;
  product_name?: string | null;
  premium_total?: string | number | null;
  premium_gross?: string | number | null;
  /** A health plan's sum insured; drawn only in the plan comparison. */
  sum_insured?: string | number | null;
  /** The IDV this price was computed on, and the insurer's accepted band. */
  idv?: string | number | null;
  min_idv?: string | number | null;
  max_idv?: string | number | null;
  /** The customer's IDV was moved into the insurer's band. */
  idv_adjusted?: boolean;
  /** Per requested add-on: included (with premium) / not offered / not online. */
  addon_cover?: AddonCover | null;
}

type ColumnKey = CoverType | 'other';

/**
 * 25 Sep -- the owner: "we need to show people what was declined". An insurer
 * that was asked and gave no price, from the server's pricing summary. Without
 * `cover_type` the insurer declined every cover; with it, just that one.
 */
export interface DeclinedQuote {
  insurer_name: string;
  reason: string;
  cover_type?: string | null;
}

/**
 * 25 Sep -- "show the page and then populate the quote". An insurer x cover
 * the server is still asking (`pricing_summary.units`, state `pending`): drawn
 * as a "Getting price…" cell until its price or decline lands.
 */
export interface PendingQuote {
  insurer_name: string;
  cover_type?: string | null;
}

const OTHER_LABEL = 'Other / as quoted';

const COLUMN_LABELS: Record<ColumnKey, string> = { ...COVER_LABELS, other: OTHER_LABEL };

/** Below Tailwind's `sm`: one cover at a time instead of the table. */
const MOBILE_QUERY = '(max-width: 639px)';

function columnOf(option: { cover_type?: string | null }): ColumnKey {
  return (COVER_ORDER as readonly string[]).includes(option.cover_type ?? '')
    ? (option.cover_type as CoverType)
    : 'other';
}


interface InsurerRow<T> {
  insurer: string;
  logo: string | null;
  cells: Partial<Record<ColumnKey, T[]>>;
  /** Why this insurer has no price in a column (a cover it declined). */
  declined: Partial<Record<ColumnKey, string>>;
  /** Why it has no price at all, when it declined every cover. */
  declinedAll: string | null;
  /** Covers this insurer is still being asked for. */
  pending: Partial<Record<ColumnKey, true>>;
}

/** A stable empty list, so the memo below is not rebuilt on every render. */
const NO_DECLINES: readonly DeclinedQuote[] = [];
const NO_PENDING: readonly PendingQuote[] = [];

/** "Third-Party only: X could not..." -> "X could not...": the column already names the cover. */
function withoutCoverPrefix(reason: string): string {
  const colon = reason.indexOf(': ');
  if (colon > 0 && colon < 40 && Object.values(COVER_LABELS).includes(reason.slice(0, colon) as never)) {
    return reason.slice(colon + 2);
  }
  return reason;
}

function cheapest(options: readonly ComparableOption[] | undefined): number | null {
  let best: number | null = null;
  for (const option of options ?? []) {
    const value = premiumOf(option);
    if (value !== null && (best === null || value < best)) best = value;
  }
  return best;
}

/** Group by insurer; which columns exist; the lowest premium in each. */
function buildComparison<T extends ComparableOption>(
  options: readonly T[],
  declines: readonly DeclinedQuote[] = [],
  pending: readonly PendingQuote[] = []
) {
  const rows = new Map<string, InsurerRow<T>>();
  const present = new Set<ColumnKey>();
  const rowFor = (insurer: string) => {
    let row = rows.get(insurer);
    if (!row) {
      row = { insurer, logo: null, cells: {}, declined: {}, declinedAll: null, pending: {} };
      rows.set(insurer, row);
    }
    return row;
  };
  for (const option of options) {
    const column = columnOf(option);
    present.add(column);
    const row = rowFor(option.insurer_name);
    row.logo = row.logo || option.insurer_logo || null;
    (row.cells[column] ??= []).push(option);
  }
  // Still being asked: a row per insurer, a "Getting price…" cell per cover.
  for (const item of pending) {
    const insurer = (item.insurer_name || '').trim();
    if (!insurer) continue;
    const column = columnOf(item);
    present.add(column);
    const row = rowFor(insurer);
    if (!row.cells[column]?.length) row.pending[column] = true;
  }
  // Declines only draw where there is a table to draw them in: a decline with
  // no priced option anywhere is left to the page's own "no prices" message.
  if (options.length > 0 || pending.length > 0) {
    for (const decline of declines) {
      const insurer = (decline.insurer_name || '').trim();
      if (!insurer || !decline.reason) continue;
      const row = rowFor(insurer);
      if (decline.cover_type) {
        const column = columnOf(decline);
        if (!row.cells[column]?.length) row.declined[column] = withoutCoverPrefix(decline.reason);
      } else if (Object.keys(row.cells).length === 0) {
        row.declinedAll = decline.reason;
      }
    }
  }
  // Cheapest first inside a cell too, when one insurer quoted a cover twice.
  for (const row of rows.values()) {
    for (const cell of Object.values(row.cells)) {
      cell?.sort((a, b) => (premiumOf(a) ?? Infinity) - (premiumOf(b) ?? Infinity));
    }
  }
  const columns = ([...COVER_ORDER, 'other'] as ColumnKey[]).filter((c) => present.has(c));
  const lowest: Partial<Record<ColumnKey, number>> = {};
  for (const column of columns) {
    const priced = options.filter((o) => columnOf(o) === column && premiumOf(o) !== null);
    // "Lowest" means something only when there is something to be lower than.
    if (priced.length >= 2) lowest[column] = cheapest(priced)!;
  }
  return { rows: [...rows.values()], columns, lowest };
}

/** Rows priced in `column` first, cheapest first; then rows still being
 *  priced; then the rest (declined), by their own cheapest price, then name. */
function sortRows<T extends ComparableOption>(rows: InsurerRow<T>[], column: ColumnKey) {
  const all = (row: InsurerRow<T>) => cheapest(Object.values(row.cells).flat() as T[]);
  const rank = (row: InsurerRow<T>) =>
    cheapest(row.cells[column]) !== null ? 0 : row.pending[column] || Object.keys(row.pending).length ? 1 : 2;
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const pa = cheapest(a.cells[column]);
    const pb = cheapest(b.cells[column]);
    if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    const oa = all(a) ?? Infinity;
    const ob = all(b) ?? Infinity;
    if (oa !== ob) return oa - ob;
    return a.insurer.localeCompare(b.insurer);
  });
}

/** The same footprint as a priced cell, so a landing price does not jump the row. */
function GettingPrice() {
  return (
    <div data-pending="true" className="px-2 py-1.5" role="status">
      <span className="block h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
      <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">Getting price…</span>
    </div>
  );
}

function NotAvailable({ reason }: { reason: string }) {
  return (
    <div data-declined="true" className="px-2 text-xs text-gray-500 dark:text-gray-400">
      <span className="font-medium text-gray-600 dark:text-gray-300">Not available online</span>
      <span className="mt-0.5 block">{reason}</span>
    </div>
  );
}

function Split({ option }: { option: ComparableOption }) {
  const od = Number(option.premium_breakdown?.od_premium ?? 0);
  const tp = Number(option.premium_breakdown?.tp_premium ?? 0);
  if (!(od > 0) && !(tp > 0)) return null;
  const words = [
    od > 0 ? `Own damage ${formatCurrency(od)}` : null,
    tp > 0 ? `Third party ${formatCurrency(tp)}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <span className="block text-[11px] leading-4 text-gray-500 dark:text-gray-400" title={words}>
      {od > 0 && `OD ${formatCurrency(od)}`}
      {od > 0 && tp > 0 && ' · '}
      {tp > 0 && `TP ${formatCurrency(tp)}`}
    </span>
  );
}

function positive(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** "IDV ₹3,00,000" under a price, with the insurer's range as its tooltip. */
function IdvLine({ option }: { option: ComparableOption }) {
  const idv = positive(option.idv);
  if (idv === null) return null;
  const low = positive(option.min_idv);
  const high = positive(option.max_idv);
  const range = low !== null && high !== null ? `Insurer's range ${formatCurrency(low)} – ${formatCurrency(high)}` : undefined;
  return (
    <span className="block text-[11px] leading-4 text-gray-600 dark:text-gray-300" title={range}>
      IDV {formatCurrency(idv)}
      {range && <span className="sr-only"> ({range})</span>}
    </span>
  );
}

/**
 * Owner, 25 Sep: "some insurance companies offer, others don't -- that should
 * be clear by showing what is available and what is not". One line per
 * add-on the customer asked for: included (and at what premium), not offered,
 * or not available online. The insurer's own name for it (often a bundle) is
 * the tooltip, so the line stays short enough for a phone.
 */
export function OptionExtras({ option }: { option: ComparableOption }) {
  const entries = addonEntries(option.addon_cover);
  if (!entries.length && !option.idv_adjusted) return null;
  return (
    <div className="mt-1 px-2 text-[11px] leading-4" data-testid="option-extras">
      {option.idv_adjusted && (
        <p className="text-amber-700 dark:text-amber-300">IDV adjusted to the insurer&rsquo;s range</p>
      )}
      {entries.length > 0 && (
        <ul aria-label={`Add-ons from ${option.insurer_name}`} className="space-y-0.5">
          {entries.map(([code, entry]) => {
            const name = addonLabel(code);
            const premium = positive(entry.premium);
            const title = entry.label && entry.label !== name ? entry.label : undefined;
            if (entry.status === 'included') {
              return (
                <li key={code} data-addon={code} data-status="included" title={title}
                  className="text-green-700 dark:text-green-300">
                  <span aria-hidden="true">✓ </span>
                  {name}
                  {premium !== null ? ` ${formatCurrency(premium)}` : ' (included)'}
                  <span className="sr-only">: included{title ? ` as ${title}` : ''}</span>
                </li>
              );
            }
            if (entry.status === 'not_offered') {
              return (
                <li key={code} data-addon={code} data-status="not_offered" title={title}
                  className="text-gray-500 line-through decoration-gray-400 dark:text-gray-400">
                  <span aria-hidden="true">✗ </span>
                  {name}
                  <span className="sr-only">: not offered</span>
                </li>
              );
            }
            return (
              <li key={code} data-addon={code} data-status="not_online" title={title}
                className="text-amber-700 dark:text-amber-300">
                <span aria-hidden="true">– </span>
                {name}: not available online
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AdvisorMark() {
  return (
    <span className="mt-0.5 inline-block rounded-full bg-blue-100 px-1.5 py-px text-[10px] font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
      Added by your advisor
    </span>
  );
}

function LowestMark() {
  return (
    <span className="ml-1 inline-block rounded-full bg-green-100 px-1.5 py-px align-middle text-[10px] font-semibold uppercase tracking-wide text-green-800 dark:bg-green-900/40 dark:text-green-200">
      Lowest
    </span>
  );
}

interface ChoiceProps {
  option: ComparableOption;
  column: ColumnKey;
  /** Overrides the column's label in the radio's accessible name. */
  label?: string;
  isLowest: boolean;
  selected: boolean;
  canChange: boolean;
  onSelect: (id: string) => void;
}

/** One priced option: the premium, its split, its markers and its radio. */
function Choice({ option, column, label, isLowest, selected, canChange, onSelect }: ChoiceProps) {
  const premium = premiumOf(option);
  const name = `${option.insurer_name} — ${label ?? COLUMN_LABELS[column]}${
    premium !== null ? `, ${formatCurrency(premium)}` : ''
  }`;
  return (
    <label
      className={cn(
        'flex items-start gap-2 rounded-lg border-2 px-2 py-1.5 text-left transition-colors',
        selected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : isLowest
            ? 'border-green-200 bg-green-50/60 dark:border-green-800 dark:bg-green-900/10'
            : 'border-transparent',
        canChange && !selected && 'cursor-pointer hover:border-gray-200 dark:hover:border-gray-600'
      )}
    >
      {canChange && (
        <input
          type="radio"
          name="quote-option"
          aria-label={name}
          className="mt-1 h-4 w-4 shrink-0 accent-primary-600"
          checked={selected}
          onChange={() => onSelect(option.id)}
        />
      )}
      <span className="min-w-0">
        <span className="block whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
          {premium !== null ? formatCurrency(premium) : 'On request'}
          {isLowest && <LowestMark />}
        </span>
        <Split option={option} />
        <IdvLine option={option} />
        {option.product_name && (
          <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">
            {option.product_name}
          </span>
        )}
        {option.pricing_source === 'manual' && <AdvisorMark />}
      </span>
    </label>
  );
}

function sumInsuredOf(option: ComparableOption): number | null {
  const value = Number(option.sum_insured);
  return option.sum_insured != null && option.sum_insured !== '' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/**
 * Health / travel (24 Sep): there are no motor covers to lay out in columns,
 * so the same options are ONE list -- insurer, plan, sum insured when any
 * option carries one, and the quoted premium with its radio -- cheapest first.
 * Same `Choice` cell, same one-quote-id hand-off to the parent.
 */
function PlanComparison<T extends ComparableOption>({
  options,
  selectedId,
  canChange,
  onSelect,
}: {
  options: readonly T[];
  selectedId: string | null;
  canChange: boolean;
  onSelect: (id: string) => void;
}) {
  const sorted = useMemo(
    () => [...options].sort((a, b) => (premiumOf(a) ?? Infinity) - (premiumOf(b) ?? Infinity)),
    [options]
  );
  const priced = options.filter((o) => premiumOf(o) !== null);
  const lowest = priced.length >= 2 ? cheapest(priced) : null;
  const showSumInsured = options.some((o) => sumInsuredOf(o) !== null);

  return (
    <div>
      <ul aria-label="Compare quotes" className="space-y-2">
        {sorted.map((option) => {
          const sumInsured = sumInsuredOf(option);
          return (
            <li
              key={option.id}
              data-insurer={option.insurer_name}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm dark:bg-gray-800"
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <InsurerLogo src={option.insurer_logo ?? null} name={option.insurer_name} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                    {option.insurer_name}
                  </span>
                  {showSumInsured && (
                    <span className="block text-xs text-gray-600 dark:text-gray-400">
                      Sum insured {sumInsured !== null ? formatCurrency(sumInsured) : '—'}
                    </span>
                  )}
                </span>
              </span>
              <span className="shrink-0">
                <Choice
                  option={option}
                  column="other"
                  label="Quoted premium"
                  isLowest={lowest !== null && premiumOf(option) === lowest}
                  selected={selectedId === option.id}
                  canChange={canChange}
                  onSelect={onSelect}
                />
              </span>
            </li>
          );
        })}
      </ul>
      {options.some((o) => o.pricing_source === 'manual') && (
        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Prices marked as your advisor&rsquo;s were prepared by them from the insurer&rsquo;s own
          quote -- ask them what each plan includes.
        </p>
      )}
    </div>
  );
}

export function QuoteComparison<T extends ComparableOption>({
  options,
  selectedId,
  canChange,
  onSelect,
  productLine = 'motor',
  declines = NO_DECLINES,
  pending = NO_PENDING,
}: {
  options: readonly T[];
  /** Insurers that were asked and gave no price, drawn as rows below the priced ones. */
  declines?: readonly DeclinedQuote[];
  /** Insurer x cover still being priced, drawn as "Getting price…" cells. */
  pending?: readonly PendingQuote[];
  /** The option to draw as chosen: the pending pick, else the accepted one. */
  selectedId: string | null;
  canChange: boolean;
  onSelect: (id: string) => void;
  /** `motor` draws covers as columns; anything else is a plan list. */
  productLine?: string;
}) {
  if (productLine !== 'motor') {
    return (
      <PlanComparison options={options} selectedId={selectedId} canChange={canChange} onSelect={onSelect} />
    );
  }
  return (
    <MotorComparison
      options={options}
      declines={declines}
      pending={pending}
      selectedId={selectedId}
      canChange={canChange}
      onSelect={onSelect}
    />
  );
}

function MotorComparison<T extends ComparableOption>({
  options,
  declines,
  pending,
  selectedId,
  canChange,
  onSelect,
}: {
  options: readonly T[];
  declines: readonly DeclinedQuote[];
  pending: readonly PendingQuote[];
  selectedId: string | null;
  canChange: boolean;
  onSelect: (id: string) => void;
}) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { rows, columns, lowest } = useMemo(
    () => buildComparison(options, declines, pending),
    [options, declines, pending]
  );
  const [pickedColumn, setPickedColumn] = useState<ColumnKey>('comprehensive');
  // A column that is not (or no longer) on the table falls back to the first.
  const active: ColumnKey = columns.includes(pickedColumn) ? pickedColumn : columns[0];
  const sorted = useMemo(() => sortRows(rows, active), [rows, active]);

  const choice = (option: T, column: ColumnKey) => (
    <Choice
      key={option.id}
      option={option}
      column={column}
      isLowest={lowest[column] !== undefined && premiumOf(option) === lowest[column]}
      selected={selectedId === option.id}
      canChange={canChange}
      onSelect={onSelect}
    />
  );

  const anyLive = options.some((o) => o.pricing_source === 'insurer_api');
  const legend = (
    <dl className="mt-3 space-y-1 text-xs text-gray-600 dark:text-gray-400">
      {anyLive && (
        <div>
          <dt className="sr-only">Where the prices come from</dt>
          <dd>Every price here is a live price from the insurer unless it is marked as your advisor&rsquo;s.</dd>
        </div>
      )}
      {columns.map((column) =>
        column === 'other' ? (
          <div key={column}>
            <dt className="inline font-medium text-gray-700 dark:text-gray-300">{OTHER_LABEL}: </dt>
            <dd className="inline">priced by your advisor without a cover type recorded -- ask them what it includes.</dd>
          </div>
        ) : (
          <div key={column}>
            <dt className="inline font-medium text-gray-700 dark:text-gray-300">{COVER_LABELS[column]}: </dt>
            <dd className="inline">{COVER_DESCRIPTIONS[column]}</dd>
          </div>
        )
      )}
    </dl>
  );

  if (isMobile) {
    const inColumn = sorted.flatMap((row) =>
      (row.cells[active] ?? []).map((option) => ({ row, option }))
    );
    return (
      <div>
        {columns.length > 1 && (
          <div
            role="group"
            aria-label="Cover type"
            className="mb-3 flex w-full rounded-lg bg-gray-100 p-1 dark:bg-gray-800"
          >
            {columns.map((column) => (
              <button
                key={column}
                type="button"
                aria-pressed={active === column}
                onClick={() => setPickedColumn(column)}
                className={cn(
                  'min-w-0 flex-1 rounded-md px-2 py-1.5 text-xs font-medium leading-tight transition-colors',
                  active === column
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                )}
              >
                {COLUMN_LABELS[column]}
              </button>
            ))}
          </div>
        )}
        <ul aria-label={`${COLUMN_LABELS[active]} quotes`} className="space-y-2">
          {inColumn.map(({ row, option }) => (
            <li
              key={option.id}
              data-insurer={row.insurer}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl bg-white p-3 shadow-sm dark:bg-gray-800"
            >
              <span className="flex min-w-0 items-center gap-2">
                <InsurerLogo src={row.logo} name={row.insurer} size="sm" />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {row.insurer}
                </span>
              </span>
              <span className="shrink-0">{choice(option, active)}</span>
              <span className="basis-full">
                <OptionExtras option={option} />
              </span>
            </li>
          ))}
          {sorted
            .filter((row) => !row.cells[active]?.length && row.pending[active])
            .map((row) => (
              <li
                key={`pending-${row.insurer}`}
                data-insurer={row.insurer}
                className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm dark:bg-gray-800"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <InsurerLogo src={row.logo} name={row.insurer} size="sm" />
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-white">{row.insurer}</span>
                </span>
                <span className="shrink-0">
                  <GettingPrice />
                </span>
              </li>
            ))}
          {sorted
            .filter(
              (row) =>
                !row.cells[active]?.length && !row.pending[active] && (row.declined[active] || row.declinedAll)
            )
            .map((row) => (
              <li
                key={`declined-${row.insurer}`}
                data-insurer={row.insurer}
                className="flex items-start gap-2 rounded-xl bg-gray-50 p-3 dark:bg-gray-800/60"
              >
                <InsurerLogo src={row.logo} name={row.insurer} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-700 dark:text-gray-300">
                    {row.insurer}
                  </span>
                  <NotAvailable reason={(row.declined[active] ?? row.declinedAll)!} />
                </span>
              </li>
            ))}
        </ul>
        {legend}
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-xl bg-white shadow-sm dark:bg-gray-800">
        <table aria-label="Compare quotes" className="w-full min-w-[28rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th
                scope="col"
                data-cover="insurer"
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Insurer
              </th>
              {columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  data-cover={column}
                  aria-sort={active === column ? 'ascending' : 'none'}
                  className="px-2 py-2 text-left align-bottom"
                >
                  <button
                    type="button"
                    onClick={() => setPickedColumn(column)}
                    aria-label={`Sort by ${COLUMN_LABELS[column]}`}
                    title={column === 'other' ? undefined : COVER_DESCRIPTIONS[column]}
                    className={cn(
                      'inline-flex items-center gap-1 rounded text-xs font-semibold uppercase tracking-wide',
                      active === column
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                    )}
                  >
                    {COLUMN_LABELS[column]}
                    {active === column ? (
                      <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <ArrowsUpDownIcon className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.insurer}
                className="border-b border-gray-100 last:border-0 dark:border-gray-700/60"
              >
                <th scope="row" data-insurer={row.insurer} className="px-3 py-2 text-left align-top">
                  <span className="flex items-center gap-2">
                    <InsurerLogo src={row.logo} name={row.insurer} size="sm" />
                    <span className="font-medium text-gray-900 dark:text-white">{row.insurer}</span>
                  </span>
                </th>
                {columns.map((column) => {
                  const cell = row.cells[column];
                  return (
                    <td key={column} data-cover={column} className="px-2 py-2 align-top">
                      {cell && cell.length > 0 ? (
                        <div className="space-y-1">
                          {cell.map((option) => (
                            <div key={option.id}>
                              {choice(option, column)}
                              <OptionExtras option={option} />
                            </div>
                          ))}
                        </div>
                      ) : row.pending[column] ? (
                        <GettingPrice />
                      ) : row.declined[column] || row.declinedAll ? (
                        <NotAvailable reason={(row.declined[column] ?? row.declinedAll)!} />
                      ) : (
                        <span className="px-2 text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {legend}
    </div>
  );
}
